import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import type { CorrectiveActionView, QualityRecordDetailView } from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { qualityApi, usersApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  StatusBadge,
  useToast,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { ActDialog, type ActField } from './ActDialog.js';
import { actionStatusTone, browserToday, isolate, qualityStatusTone } from './operations-ui.js';

type RecordAct = 'edit' | 'close' | 'cancel' | 'addAction';
type ActionAct = { readonly act: 'complete' | 'verify' | 'cancel'; readonly action: CorrectiveActionView };

/**
 * One quality record and its corrective actions (Phase 12, D163–D165).
 *
 * The acts offered come from the server's `can` hints; each endpoint re-checks
 * the permission, the state, and — for verification — that the caller did not
 * complete the action themselves.
 */
export function QualityRecordPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const locale = currentLocale();

  const [recordAct, setRecordAct] = useState<RecordAct | null>(null);
  const [actionAct, setActionAct] = useState<ActionAct | null>(null);

  const record = useQuery({ queryKey: ['quality-record', id], queryFn: () => qualityApi.get(id) });
  const people = useQuery({
    queryKey: ['users', 'selectable'],
    queryFn: () => usersApi.list({ pageSize: 100 }),
    enabled: (recordAct === 'edit' || recordAct === 'addAction') && can(PERMISSIONS.USERS_VIEW),
  });

  if (record.isLoading) return <LoadingState label={t('common.loading')} />;
  if (record.error) return <ErrorState error={record.error} onRetry={() => void record.refetch()} />;
  const data = record.data;
  if (!data) return null;

  const refresh = async (next?: QualityRecordDetailView) => {
    if (next) queryClient.setQueryData(['quality-record', id], next);
    await queryClient.invalidateQueries({ queryKey: ['quality-record', id] });
    await queryClient.invalidateQueries({ queryKey: ['quality-records'] });
    await queryClient.invalidateQueries({ queryKey: ['corrective-actions'] });
  };
  const date = (value: string | null) => (value ? (formatDate(value, { locale }) ?? value) : t('common.notSet'));
  const personOptions = (people.data?.data ?? [])
    .filter((person) => person.isActive)
    .map((person) => ({ value: person.id, label: person.displayName }));

  const recordDialogs: Record<RecordAct, { title: string; hint?: string; submit: string; destructive?: boolean; fields: ActField[]; run: (v: Record<string, string | null>) => Promise<QualityRecordDetailView> }> = {
    edit: {
      title: t('quality.editTitle'),
      submit: t('common.save'),
      fields: [
        { name: 'responsibleUserId', label: t('quality.responsible'), kind: 'select', options: personOptions, initial: data.responsibleUser?.id ?? null },
        { name: 'dueOn', label: t('quality.dueOn'), kind: 'date', initial: data.dueOn },
        { name: 'rootCause', label: t('quality.rootCause'), kind: 'textarea', initial: data.rootCause },
      ],
      run: (v) =>
        qualityApi.update(id, {
          responsibleUserId: v.responsibleUserId ?? null,
          dueOn: v.dueOn ?? null,
          rootCause: v.rootCause?.trim() || null,
        }),
    },
    close: {
      title: t('quality.closeTitle'),
      hint: t('quality.closeHint'),
      submit: t('quality.close'),
      fields: [
        { name: 'closureSummary', label: t('quality.closureSummary'), kind: 'textarea', isRequired: true },
        { name: 'closedOn', label: t('quality.closedOn'), kind: 'date', isRequired: true, initial: browserToday() },
      ],
      run: (v) => qualityApi.close(id, { closureSummary: v.closureSummary?.trim(), closedOn: v.closedOn }),
    },
    cancel: {
      title: t('quality.cancelTitle'),
      hint: t('quality.cancelHint'),
      submit: t('quality.cancel'),
      destructive: true,
      fields: [{ name: 'reason', label: t('common.reason'), kind: 'textarea', isRequired: true }],
      run: (v) => qualityApi.cancel(id, v.reason?.trim() ?? ''),
    },
    addAction: {
      title: t('quality.addActionTitle'),
      submit: t('quality.addAction'),
      fields: [
        { name: 'description', label: t('quality.actionDescription'), kind: 'textarea', isRequired: true },
        { name: 'responsibleUserId', label: t('quality.responsible'), kind: 'select', isRequired: true, options: personOptions },
        { name: 'dueOn', label: t('quality.dueOn'), kind: 'date', isRequired: true },
      ],
      run: (v) =>
        qualityApi.addAction(id, {
          description: v.description?.trim(),
          responsibleUserId: v.responsibleUserId,
          dueOn: v.dueOn,
        }),
    },
  };

  const actionDialog = actionAct
    ? actionAct.act === 'complete'
      ? {
          title: t('quality.completeTitle'),
          submit: t('quality.complete'),
          destructive: false,
          fields: [
            { name: 'completionNote', label: t('quality.completionNote'), kind: 'textarea' as const, isRequired: true },
            { name: 'completedOn', label: t('quality.completedOn'), kind: 'date' as const, isRequired: true, initial: browserToday() },
          ],
          run: (v: Record<string, string | null>) =>
            qualityApi.completeAction(actionAct.action.id, { completionNote: v.completionNote?.trim(), completedOn: v.completedOn }),
        }
      : actionAct.act === 'verify'
        ? {
            title: t('quality.verifyTitle'),
            hint: t('quality.verifyHint'),
            submit: t('quality.verify'),
            destructive: false,
            fields: [
              { name: 'verificationNote', label: t('quality.verificationNote'), kind: 'textarea' as const, isRequired: true },
              { name: 'verifiedOn', label: t('quality.verifiedOn'), kind: 'date' as const, isRequired: true, initial: browserToday() },
            ],
            run: (v: Record<string, string | null>) =>
              qualityApi.verifyAction(actionAct.action.id, { verificationNote: v.verificationNote?.trim(), verifiedOn: v.verifiedOn }),
          }
        : {
            title: t('quality.cancelActionTitle'),
            submit: t('quality.cancelAction'),
            destructive: true,
            fields: [{ name: 'reason', label: t('common.reason'), kind: 'textarea' as const, isRequired: true }],
            run: (v: Record<string, string | null>) => qualityApi.cancelAction(actionAct.action.id, v.reason?.trim() ?? ''),
          }
    : null;

  const current = recordAct ? recordDialogs[recordAct] : null;

  return (
    <>
      <PageHeader
        title={`${data.recordNumber} — ${data.title}`}
        subtitle={t(`qualityKind.${data.kind}`)}
        breadcrumbs={[{ label: t('nav.quality'), to: '/quality' }, { label: data.recordNumber }]}
        actions={
          <>
            {data.can.start ? (
              <ActionButton
                priority="secondary"
                onPress={async () => {
                  try {
                    await refresh(await qualityApi.start(id));
                    show({ tone: 'success', message: t('quality.started') });
                  } catch (error) {
                    // What the server said, in words; nothing changed on screen.
                    const code = error instanceof ApiError ? error.code : 'unknown';
                    show({ tone: 'danger', message: t(`errors.${code}`, { defaultValue: t('errors.unknown') }) });
                  }
                }}
              >
                {t('quality.start')}
              </ActionButton>
            ) : null}
            {data.can.edit ? (
              <ActionButton priority="secondary" onPress={() => setRecordAct('edit')}>
                {t('common.edit')}
              </ActionButton>
            ) : null}
            {data.can.cancel ? (
              <ActionButton priority="tertiary" onPress={() => setRecordAct('cancel')}>
                {t('quality.cancel')}
              </ActionButton>
            ) : null}
            {data.can.close ? (
              <ActionButton priority="primary" onPress={() => setRecordAct('close')}>
                {t('quality.close')}
              </ActionButton>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-gutter lg:grid-cols-2">
        <Panel title={t('quality.detailsTitle')}>
          <div className="flex flex-col gap-stack">
            <ReadOnlyField label={t('common.status')}>
              <span className="flex flex-wrap gap-inline">
                <StatusBadge label={t(`qualityStatus.${data.status}`)} tone={qualityStatusTone(data.status)} />
                {data.isOverdue ? <StatusBadge label={t('quality.overdue')} tone="danger" /> : null}
              </span>
            </ReadOnlyField>
            <ReadOnlyField label={t('quality.description')}>
              <span className="whitespace-pre-wrap" dir="auto">{data.description}</span>
            </ReadOnlyField>
            <ReadOnlyField label={t('quality.raisedOn')}>
              {t('quality.raisedByOn', { name: isolate(data.raisedBy.displayName), date: isolate(date(data.raisedOn)) })}
            </ReadOnlyField>
            <ReadOnlyField label={t('quality.responsible')}>
              {data.responsibleUser?.displayName ?? t('common.notSet')}
            </ReadOnlyField>
            <ReadOnlyField label={t('quality.dueOn')}>{date(data.dueOn)}</ReadOnlyField>
            <ReadOnlyField label={t('quality.rootCause')}>
              <span className="whitespace-pre-wrap" dir="auto">{data.rootCause ?? t('common.notRecorded')}</span>
            </ReadOnlyField>
            {data.client ? (
              <ReadOnlyField label={t('quality.client')}>
                <Link className="text-primary-800 underline" to={`/clients/${data.client.id}`} dir="ltr">
                  {data.client.label}
                </Link>
              </ReadOnlyField>
            ) : null}
            {data.equipment ? (
              <ReadOnlyField label={t('quality.equipment')}>
                <Link className="text-primary-800 underline" to={`/equipment/${data.equipment.id}`} dir="ltr">
                  {data.equipment.label}
                </Link>
              </ReadOnlyField>
            ) : null}
          </div>
        </Panel>

        {data.status === 'closed' || data.status === 'cancelled' ? (
          <Panel title={data.status === 'closed' ? t('quality.closureTitle') : t('quality.cancellationTitle')}>
            <div className="flex flex-col gap-stack">
              {data.status === 'closed' ? (
                <>
                  <ReadOnlyField label={t('quality.closureSummary')}>
                    <span className="whitespace-pre-wrap" dir="auto">{data.closureSummary}</span>
                  </ReadOnlyField>
                  <ReadOnlyField label={t('quality.closedOn')}>
                    {t('quality.closedByOn', { name: isolate(data.closedBy?.displayName), date: isolate(date(data.closedOn)) })}
                  </ReadOnlyField>
                </>
              ) : (
                <ReadOnlyField label={t('common.reason')}>{data.cancellationReason}</ReadOnlyField>
              )}
              <p className="text-caption text-default-600">{t('quality.notAVerdict')}</p>
            </div>
          </Panel>
        ) : null}
      </div>

      <div className="mt-gutter">
        <Panel
          title={t('quality.actionsTitle')}
          description={t('quality.actionsHint')}
          actions={
            data.can.addAction ? (
              <ActionButton priority="secondary" onPress={() => setRecordAct('addAction')}>
                {t('quality.addAction')}
              </ActionButton>
            ) : null
          }
        >
          {data.correctiveActions.length === 0 ? (
            <p className="text-body text-default-700">{t('quality.noActions')}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-default-100">
              {data.correctiveActions.map((action) => (
                <li key={action.id} className="flex flex-col gap-2 py-3">
                  <div className="flex flex-wrap items-center gap-inline">
                    <StatusBadge label={t(`correctiveActionStatus.${action.status}`)} tone={actionStatusTone(action.status)} />
                    {action.isOverdue ? <StatusBadge label={t('quality.overdue')} tone="danger" /> : null}
                    <span className="min-w-0 flex-1 text-body text-default-900">{action.description}</span>
                  </div>
                  <p className="text-caption text-default-700">
                    {t('quality.actionLine', {
                      name: isolate(action.responsibleUser.displayName),
                      date: isolate(date(action.dueOn)),
                    })}
                  </p>
                  {action.completionNote ? (
                    <p className="text-caption text-default-700">
                      {t('quality.completedLine', {
                        name: isolate(action.completedBy?.displayName),
                        date: isolate(date(action.completedOn)),
                        note: isolate(action.completionNote),
                      })}
                    </p>
                  ) : null}
                  {action.verificationNote ? (
                    <p className="text-caption text-default-700">
                      {t('quality.verifiedLine', {
                        name: isolate(action.verifiedBy?.displayName),
                        date: isolate(date(action.verifiedOn)),
                        note: isolate(action.verificationNote),
                      })}
                    </p>
                  ) : null}
                  {action.cancellationReason ? (
                    <p className="text-caption text-default-700">
                      {t('quality.cancelledLine', { reason: isolate(action.cancellationReason) })}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-inline">
                    {action.can.complete ? (
                      <ActionButton size="sm" priority="secondary" onPress={() => setActionAct({ act: 'complete', action })}>
                        {t('quality.complete')}
                      </ActionButton>
                    ) : null}
                    {action.can.verify ? (
                      <ActionButton size="sm" priority="secondary" onPress={() => setActionAct({ act: 'verify', action })}>
                        {t('quality.verify')}
                      </ActionButton>
                    ) : null}
                    {action.can.cancel ? (
                      <ActionButton size="sm" priority="tertiary" onPress={() => setActionAct({ act: 'cancel', action })}>
                        {t('quality.cancelAction')}
                      </ActionButton>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {current ? (
        <ActDialog
          isOpen
          onClose={() => setRecordAct(null)}
          title={current.title}
          {...(current.hint ? { hint: current.hint } : {})}
          submitLabel={current.submit}
          destructive={current.destructive ?? false}
          fields={current.fields}
          onSubmit={async (values) => {
            await refresh(await current.run(values));
            show({ tone: 'success', message: t('quality.saved') });
          }}
        />
      ) : null}

      {actionDialog ? (
        <ActDialog
          isOpen
          onClose={() => setActionAct(null)}
          title={actionDialog.title}
          {...('hint' in actionDialog && actionDialog.hint ? { hint: actionDialog.hint } : {})}
          submitLabel={actionDialog.submit}
          destructive={actionDialog.destructive}
          fields={actionDialog.fields}
          onSubmit={async (values) => {
            await actionDialog.run(values);
            await refresh();
            show({ tone: 'success', message: t('quality.saved') });
          }}
        />
      ) : null}
    </>
  );
}
